import Button, { NavigableButton } from '@carvana/ds-button';
import cx from 'classnames';
import { type ReactNode, useContext } from 'react';
import Image from '@/common/Image';
import RollingNumber from '@/common/RollingNumber';
import SectionName from '~/constants/new-cars/SectionName';
import { useConfiguration } from '~/context/common/StaticContext';
import { useModalContext } from '~/context/new-cars/ModalContext/ModalContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import useAvailableCount from '~/hooks/new-cars/selectors/useAvailableCount';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import useDisplayPrice from '~/hooks/new-cars/selectors/useDisplayPrice';
import useMatchingVehiclesData from '~/hooks/new-cars/selectors/useMatchingVehiclesData';
import useSelectionSummary from '~/hooks/new-cars/selectors/useSelectionSummary';
import useVehicleImageUrl from '~/hooks/new-cars/selectors/useVehicleImageUrl';
import useVehicleName from '~/hooks/new-cars/selectors/useVehicleName';
import useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';
import FooterSelections from './FooterSelections';
import { FooterBar, FooterContent } from './StickyFooter.styles';

type OwnProps = {
  footerRef?: (node: HTMLDivElement | null) => void;
  isReleased: boolean;
  onOpen: () => void;
};

export default function Footer({ footerRef, isReleased, onOpen }: OwnProps) {
  const { activeSection, customizeOptions, isFirstSection, selectedOptions } =
    useContext(ParentModelContext);
  const { enableNewCarConfiguratorV2 } = useConfiguration();
  const availableCount = useAvailableCount();
  const closestVehicle = useClosestVehicle();
  const price = useDisplayPrice();
  const { matchingVehicles } = useMatchingVehiclesData();
  const { setViewVehiclesModalOpen } = useModalContext();
  const testDriveCenter = useTestDriveCenter();
  const customizedTrim = customizeOptions.data;
  const [rawClosestVehicle] = matchingVehicles;

  const imageUrl = useVehicleImageUrl({
    width: 250
  });
  const vehicleName = useVehicleName();
  const selectionSummary = useSelectionSummary();

  const showClosestVehicleLink =
    activeSection === SectionName.Options &&
    !closestVehicle?.vehicleId &&
    Boolean(rawClosestVehicle?.vehicleId);

  const showDefaultView =
    enableNewCarConfiguratorV2 && isFirstSection(activeSection);

  let countPillLabel: string;
  if (closestVehicle?.vehicleId) {
    countPillLabel = 'View Perfect Match';
  } else {
    countPillLabel = `${availableCount.toLocaleString()} In Stock`;
  }

  // Start Tour is a TDC CTA the V2 count pill does not replace.
  const startTourButton = (
    <Button
      className="flex-1 sm:flex-none w-full sm:w-[143px]"
      data-analytics-attributes={JSON.stringify({
        level: 'Options',
        section: 'Sticky Footer',
        text: 'Start Tour'
      })}
      data-analytics-id="New Cars TDC"
      data-testid="footer-start-tour"
      onClick={() => {
        const data = customizedTrim;
        testDriveCenter.sendBuild({
          make: data.make || selectedOptions?.make || '',
          model:
            data.model ||
            data.parentModel ||
            selectedOptions?.parentModel ||
            '',
          year: data.year || selectedOptions?.year || 0,
          trim: data.trim || selectedOptions?.trim || '',
          color: closestVehicle?.exteriorColor?.name,
          interior: closestVehicle?.interiorColor?.name,
          drivetrain: closestVehicle?.driveType?.name,
          options: (closestVehicle?.packageOptions || []).map(o => o.name)
        });
      }}
      size="sm"
      variant="primaryInverted"
    >
      Start Tour
    </Button>
  );

  let leftContent: ReactNode;
  if (showDefaultView) {
    leftContent = (
      <p className="t-heading-xs flex-1 text-text-inverse sm:t-heading-sm">
        Find your perfect match in just few clicks!
      </p>
    );
  } else if (enableNewCarConfiguratorV2) {
    leftContent = (
      <div className="flex min-w-px flex-1 items-center gap-md sm:gap-lg">
        {imageUrl && (
          <Image
            alt={vehicleName}
            className="h-[53px] w-[80px] shrink-0 rounded-md bg-background-default object-cover sm:h-[60px] sm:w-[90px]"
            height={60}
            loading="eager"
            src={imageUrl}
            width={90}
          />
        )}

        <div className="flex min-w-px flex-1 flex-col gap-md">
          <div className="flex min-w-px flex-col gap-xs">
            <p className="t-heading-sm text-text-inverse sm:t-heading-md">
              {vehicleName}
            </p>
            <FooterSelections items={selectionSummary} />
          </div>
          <p
            aria-label={`$${price.toLocaleString()}`}
            className="t-heading-sm text-text-inverse sm:t-heading-md"
            role="status"
          >
            <span aria-hidden="true" className="inline-flex items-baseline">
              <span>$</span>
              <RollingNumber key={price} value={price} />
            </span>
          </p>
        </div>
      </div>
    );
  } else {
    leftContent = (
      <div className="flex flex-1 items-center gap-md sm:gap-lg">
        {imageUrl && (
          <Image
            alt={vehicleName}
            className="order-1 h-[45px] w-[70px] shrink-0 rounded-md bg-background-default object-cover sm:order-0 sm:h-[60px] sm:w-80"
            height={60}
            loading="eager"
            src={imageUrl}
            width={80}
          />
        )}

        <div className="flex flex-1 flex-col gap-xs sm:gap-none">
          <div className="flex flex-col gap-sm sm:items-start">
            <p className="t-heading-xs text-text-inverse sm:t-heading-sm">
              {vehicleName}
            </p>
            <p
              aria-label={`$${price.toLocaleString()}`}
              className="t-body-sm text-text-inverse"
              role="status"
            >
              <span
                aria-hidden="true"
                className="t-strong-sm inline-flex items-baseline"
              >
                <span>$</span>
                <RollingNumber key={price} value={price} />
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FooterBar
      ref={footerRef}
      className={cx({
        'mb-40 rounded-bl-2xl rounded-br-2xl sm:mb-3xl lg:mb-0': isReleased
      })}
    >
      <FooterContent>
        {leftContent}

        {enableNewCarConfiguratorV2 ? (
          <div className="flex w-full gap-md sm:w-auto">
            <Button
              className="w-full sm:w-auto"
              data-analytics-attributes={JSON.stringify({
                section: 'Sticky Footer',
                text: countPillLabel
              })}
              data-analytics-id="New Cars"
              data-testid="footer-view-vehicles-cta"
              onClick={() => {
                setViewVehiclesModalOpen(true);
              }}
              size="sm"
              variant={
                closestVehicle?.vehicleId
                  ? 'primaryInverted'
                  : 'secondaryInverted'
              }
            >
              {countPillLabel}
            </Button>
            {testDriveCenter.isActive &&
              closestVehicle?.vehicleId &&
              startTourButton}
          </div>
        ) : (
          <>
            {closestVehicle?.vehicleId && (
              <div className="flex w-full gap-md sm:w-auto">
                <Button
                  className={cx(
                    'flex-1 sm:flex-none w-full sm:w-[143px]',
                    'shadow-none! border-2! border-(--decorative-2-strong)! bg-transparent! text-text-inverse!',
                    'enabled:hover:border-transparent! enabled:hover:bg-decorative-2-strong! enabled:hover:text-text-strong!',
                    'enabled:active:border-transparent! enabled:active:bg-(--icon-warning)! enabled:active:text-text-inverse!'
                  )}
                  data-analytics-attributes={JSON.stringify({
                    level: 'Options',
                    section: 'Sticky Footer',
                    text: 'Review Price'
                  })}
                  data-analytics-id="New Cars"
                  data-testid="footer-review-price"
                  onClick={onOpen}
                  size="sm"
                  variant="secondaryInverted"
                >
                  Review Price
                </Button>
                {testDriveCenter.isActive ? (
                  startTourButton
                ) : (
                  <NavigableButton
                    className="flex-1 sm:flex-none w-full sm:w-[143px]"
                    data-analytics-attributes={JSON.stringify({
                      level: 'Options',
                      section: 'Sticky Footer',
                      text: 'View Vehicle'
                    })}
                    data-analytics-id="New Cars"
                    data-testid="footer-view-vehicle"
                    href={`/vehicle/${closestVehicle.vehicleId}`}
                    size="sm"
                    variant="primaryInverted"
                  >
                    View Vehicle
                  </NavigableButton>
                )}
              </div>
            )}

            {showClosestVehicleLink && (
              <p className="t-body-sm text-text-inverse">
                <a
                  className="underline"
                  data-analytics-attributes={JSON.stringify({
                    level: SectionName.Options,
                    section: 'Sticky Footer',
                    text: 'View closest matching vehicle'
                  })}
                  data-analytics-id="New Cars"
                  data-testid="footer-closest-vehicle-link"
                  href={`/vehicle/${rawClosestVehicle?.vehicleId}`}
                >
                  View closest matching vehicle
                </a>{' '}
                ({`$${rawClosestVehicle?.incentivizedPrice.toLocaleString()}`})
              </p>
            )}
          </>
        )}
      </FooterContent>
    </FooterBar>
  );
}
